import React from 'react';
import Box from '@amzn/awsui-components-core/box';
import StatusIndicator from '@amzn/awsui-components-core/status-indicator';
import DeltaChip from './DeltaChip';
import InlineTrend from './InlineTrend';
import { getMetricStatus, getMetricStatusColor } from './metricStatus';

interface PercentageMetricCardProps {
  metricName: string;
  owner: string;
  goal: number;
  /** null entries = weeks with no observations (distinct from 0%). */
  weeklyValues: Array<number | null>;
  weekIndex: number;
  weeks: string[];
}

const STATUS_MAP = { green: 'success' as const, amber: 'warning' as const, red: 'error' as const };
const STATUS_LABELS = { green: 'On track', amber: 'At risk', red: 'Off track' };

const PercentageMetricCard: React.FC<PercentageMetricCardProps> = ({ metricName, owner, goal, weeklyValues, weekIndex, weeks }) => {
  const inRange = weekIndex >= 0 && weekIndex < weeklyValues.length;
  const value = inRange ? weeklyValues[weekIndex] : (weeklyValues[weeklyValues.length - 1] ?? null);
  // Compare against the most recent EARLIER week that has data, so a gap
  // doesn't read as a delta of zero.
  let prev: number | null = null;
  for (let i = Math.min(weekIndex, weeklyValues.length) - 1; i >= 0; i--) {
    const candidate = weeklyValues[i];
    if (typeof candidate === 'number') {
      prev = candidate;
      break;
    }
  }

  // No observations for the selected week: say so rather than showing 0% and
  // an "Off track" badge, which asserts a result we don't have.
  if (value === null) {
    return (
      <div className="otd-card" style={{ borderTop: '4px solid #8c8c94' }}>
        <div className="otd-card__top">
          <StatusIndicator type="pending">No data</StatusIndicator>
        </div>
        <div className="otd-card__body">
          <div className="otd-card__value" style={{ color: '#8c8c94' }}>—</div>
          <InlineTrend values={weeklyValues} goal={goal} color="#8c8c94" selectedIndex={weekIndex} weekLabels={weeks} />
        </div>
        <Box variant="p" fontSize="body-s" fontWeight="bold" color="text-body-secondary" padding={{ top: 'xs' }}>
          {metricName}
        </Box>
        <Box variant="p" fontSize="body-s" color="text-status-inactive">
          Owner: {owner} · Goal: {goal}%
        </Box>
      </div>
    );
  }

  const status = getMetricStatus(value, goal);
  const color = getMetricStatusColor(value, goal);

  return (
    <div className="otd-card" style={{ borderTop: `4px solid ${color}` }}>
      <div className="otd-card__top">
        <StatusIndicator type={STATUS_MAP[status]}>{STATUS_LABELS[status]}</StatusIndicator>
        <DeltaChip current={value} previous={prev} unit="pp" />
      </div>
      <div className="otd-card__body">
        <div className="otd-card__value" style={{ color }}>
          {value.toFixed(1)}<span className="otd-card__unit">%</span>
        </div>
        <InlineTrend values={weeklyValues} goal={goal} color={color} selectedIndex={weekIndex} weekLabels={weeks} />
      </div>
      <div className="otd-card__bar">
        <div className="otd-card__bar-track">
          <div className="otd-card__bar-fill" style={{ width: `${Math.min(value, 100)}%`, background: color }} />
        </div>
        <div className="otd-card__bar-scale"><span>0%</span><span>100%</span></div>
      </div>
      <Box variant="p" fontSize="body-s" fontWeight="bold" color="text-body-secondary" padding={{ top: 'xs' }}>
        {metricName}
      </Box>
      <Box variant="p" fontSize="body-s" color="text-status-inactive">
        Owner: {owner} · Goal: {goal}%
      </Box>
    </div>
  );
};

export default PercentageMetricCard;
